import { useQuery } from "@tanstack/react-query"
import * as React from "react"

import { canListUsers } from "@/lib/roles"
import type { FilterDef } from "@/lib/table"

import { assignableStaffQuery } from "../api"
import {
  assigneeFilterOptions,
  assigneeScopeFor,
  type AssigneeScope,
} from "../queue-assignee-options"
import { buildQueueFilterSchema } from "../queue-filters"

import { useViewer } from "./use-viewer"

/**
 * The filter panel for whoever is signed in, and who their requests may name
 * as assignee. Only an admin's panel ever reads `/users`.
 *
 * `ready` is false while an admin's roster is still loading. Until then their
 * Assignee options are only "Me", so a link's `?assignee=42` would be dropped
 * as unknown and the list fetched unfiltered; the page holds its queries until
 * the roster settles. A failed roster settles too, leaving "Me" only, so the
 * page is not held forever: `rosterFailedAt` says so (the failure's time, null
 * otherwise), so the page can tell the reader a linked assignee was not
 * applied rather than drop it in silence.
 */
export function useQueueFilters(): {
  schema: FilterDef[]
  assigneeScope: AssigneeScope | undefined
  ready: boolean
  rosterFailedAt: number | null
  retryRoster: () => void
} {
  const viewer = useViewer()
  const readsRoster = canListUsers(viewer?.role)
  const {
    data: roster,
    isPending,
    isError,
    errorUpdatedAt,
    refetch,
  } = useQuery(assignableStaffQuery({ enabled: readsRoster }))

  const options = React.useMemo(() => assigneeFilterOptions(viewer, roster), [viewer, roster])
  const schema = React.useMemo(() => buildQueueFilterSchema(options), [options])
  const assigneeScope = React.useMemo(() => assigneeScopeFor(viewer), [viewer])
  const retryRoster = React.useCallback(() => {
    void refetch()
  }, [refetch])
  return {
    schema,
    assigneeScope,
    ready: !readsRoster || !isPending,
    rosterFailedAt: readsRoster && isError ? errorUpdatedAt : null,
    retryRoster,
  }
}
